import { useEffect, useMemo, useState } from "react";

import { useGroups } from "GroupsManagement/hooks/useNavBarGroups";

import fuzzyMatch from "Utilities/fuzzyMatch";

import { useDebouncedSearchValue } from "../groupsCommunicationStore";

/** @returns {Partial<Record<number, boolean>>} */
export function useIsSearchHitByGroupID() {
  const { groups = [] } = useGroups();

  const trueByGroupID = useMemo(
    () =>
      groups.reduce((mTrueByGroupID, { ID }) => {
        mTrueByGroupID[ID] = true;
        return mTrueByGroupID;
      }, {}),
    [groups]
  );

  const debouncedSearchValue = useDebouncedSearchValue();
  // Initially treat every group as a search "hit"
  const [isSearchHitByGroupID, setIsSearchHitByGroupID] =
    useState(trueByGroupID);

  // In addition to using a debounced search value, we utilize a Promise-based
  // effect to keep the search input responsive and the result list updates smooth
  useEffect(() => {
    let shouldIgnore = false;

    if (debouncedSearchValue.length === 0) {
      // If there is no search, every group is a "hit"
      setIsSearchHitByGroupID(trueByGroupID);
    } else {
      Promise.all(
        groups.map(({ ID, name }) =>
          Promise.resolve([ID, fuzzyMatch(debouncedSearchValue, name)])
        )
      ).then((areSearchHits) => {
        if (!shouldIgnore) {
          setIsSearchHitByGroupID(
            areSearchHits.reduce(
              (mIsSearchHitByGroupID, [ID, isSearchHit]) => {
                mIsSearchHitByGroupID[ID] = isSearchHit;
                return mIsSearchHitByGroupID;
              },
              /** @type {Partial<Record<number, boolean>>} */ {}
            )
          );
        }
      });
    }

    return () => {
      shouldIgnore = true;
    };
  }, [debouncedSearchValue, groups, trueByGroupID]);

  return isSearchHitByGroupID;
}
